import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from '../../utils/axios';
import config from '../../config/config';
import DOMPurify from 'dompurify';

const BlogGrid = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [blogPosts, setBlogPosts] = useState([]);

  const filters = ['All', 'Amazon PPC', 'E-commerce', 'Marketing', 'Business Growth'];

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get('/api/blogs/public');
        setBlogPosts(response.data);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      }
    };

    fetchBlogs();
  }, []);

  // Function to extract excerpt from HTML content
  const getExcerpt = (content, length = 150) => {
    // Create a temporary div to parse HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(content);
    const textContent = tempDiv.textContent || tempDiv.innerText;
    return textContent.length > length 
      ? textContent.substring(0, length) + '...'
      : textContent;
  };

  // Function to get read time
  const getReadTime = (content) => {
    const wordsPerMinute = 200;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(content);
    const textContent = tempDiv.textContent || tempDiv.innerText;
    const words = textContent.trim().split(/\s+/).length;
    const readTime = Math.ceil(words / wordsPerMinute);
    return `${readTime} min read`;
  };

  const filteredPosts = activeFilter === 'All'
    ? blogPosts
    : blogPosts.filter(post => {
        const postTags = Array.isArray(post.tags) 
          ? post.tags.map(tag => tag.replace(/["\[\]]/g, '').trim())
          : [];
        return postTags.some(tag => tag.includes(activeFilter));
      });

  return (
    <section className="py-20 bg-black">
      <div className="container mx-auto px-4">
        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-4 mb-12 justify-center">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeFilter === filter
                  ? 'bg-orange-500 text-white'
                  : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        <div className="grid gap-8 md:grid-cols-2 h-full">
          {filteredPosts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group"
            >
              <Link to={`/blog/${post.slug}`} className="block h-full">
                <div className="relative bg-white/5 rounded-3xl overflow-hidden h-full">
                  <div className="flex flex-col md:flex-row h-full">
                    {/* Image Container */}
                    <div className="md:w-2/5 relative">
                      <img
                        src={post.imageUrl ? `${config.API_BASE_URL}${post.imageUrl}` : '/placeholder-image.jpg'}
                        alt={post.title}
                        className="w-full h-full min-h-[400px] object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>

                    {/* Content Container */}
                    <div className="md:w-3/5 p-8 flex flex-col justify-between">
                      {/* Top Content */}
                      <div>
                        {/* Meta Info */}
                        <div className="flex items-center gap-4 mb-4">
                          <span className="text-sm px-3 py-1 rounded-full bg-orange-500/10 text-orange-500">
                            {Array.isArray(post.tags) && post.tags.length > 0 
                              ? post.tags[0].replace(/["\[\]]/g, '').trim()
                              : 'Uncategorized'}
                          </span>
                          <span className="text-sm text-gray-400">{getReadTime(post.content)}</span>
                        </div>

                        {/* Title & Excerpt */}
                        <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-500 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-400 mb-6">
                          {getExcerpt(post.content)}
                        </p>
                      </div>

                      {/* Bottom Meta */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div>
                            <h4 className="text-white font-semibold">
                              {post.author || 'Anonymous'}
                            </h4>
                          </div>
                        </div>
                        <span className="text-sm text-gray-400">
                          {new Date(post.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}

          {filteredPosts.length === 0 && (
            <div className="col-span-2 text-center py-20">
              <p className="text-gray-400">No blog posts found for this category.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogGrid;
